import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { rememberVgsConfig } from "../utils/tokenization-config";
import { setWidgetLinkId } from "../services/api";
import type {
  ISessionState,
  IWidgetSession,
} from "../shared/session/interfaces/session";
import type { IErrorState } from "../shared/error/interfaces/error";

const SESSION_PERSIST_NAME = "raas-widgets-flow-lola-session";

interface ISessionActions {
  setSession: (session: ISessionState) => void;
  updateSession: (data: IWidgetSession) => void;
  setError: (error: IErrorState) => void;
  resetSession: () => void;
}

export const useSessionStore = create<
  { session: ISessionState } & ISessionActions
>()(
  persist(
    (set) => ({
      session: {
        data: null,
        status: RequestStatus.NOT_LOADED,
        error: null,
      },
      setSession: (session) => {
        rememberVgsConfig(session.data?.config?.vgs);
        set(() => ({ session }));
      },
      updateSession: (data) => {
        rememberVgsConfig(data.config?.vgs);
        set((state) => ({
          session: {
            ...state.session,
            data,
            status: RequestStatus.LOADED,
            error: null,
          },
        }));
      },
      setError: (error) => {
        set((state) => ({
          session: {
            ...state.session,
            status: RequestStatus.ERROR,
            error,
          },
        }));
      },
      resetSession: () => {
        set(() => ({
          session: {
            data: null,
            status: RequestStatus.NOT_LOADED,
            error: null,
          },
        }));
      },
    }),
    {
      name: SESSION_PERSIST_NAME,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        session: {
          data: state.session.data
            ? {
                ...state.session.data,
                // Partner theme is live from Superadmin on each claim. Persisting
                // it freezes `colors.inactived` (and the rest) across reloads.
                partner: state.session.data.partner
                  ? { ...state.session.data.partner, theme: undefined }
                  : state.session.data.partner,
              }
            : null,
          status: RequestStatus.NOT_LOADED,
          error: null,
        },
      }),
      onRehydrateStorage: () => (state) => {
        try {
          localStorage.removeItem(SESSION_PERSIST_NAME);
        } catch {
          // private mode / quota
        }
        if (!state?.session) return;
        rememberVgsConfig(state.session.data?.config?.vgs);
        const stored = state.session.data?.linkId;
        const urlLinkId = resolveUrlLinkId();

        if (urlLinkId && stored && urlLinkId !== stored) {
          state.session.data = null;
          state.session.status = RequestStatus.NOT_LOADED;
          state.session.error = null;
          setWidgetLinkId(null);
          return;
        }

        if (stored && (!urlLinkId || urlLinkId === stored)) {
          setWidgetLinkId(stored);
        }
        state.session.status = RequestStatus.NOT_LOADED;
        state.session.error = null;
      },
    }
  )
);
